import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CombinationFormFields } from '@/components/question/forms/combination/CombinationForm';
import {
  IsNewItemArgs,
  UseQuestionFieldArrayReturn,
} from '@/data/hook/question/answers/types';
import { useSpotlight } from '@/data/hook/shared/useSpotlight';
import { PositionalAnswerCreateDTO } from '@/models/Answer';
import { validateCombinationAnswers } from '@/utils/questions/validateAnswers';

type UseCombinationAnswersReturn =
  UseQuestionFieldArrayReturn<PositionalAnswerCreateDTO> & {
    handleUpdateValidPositions: (
      index: number,
      validPositions: number[],
    ) => void;
    handleMarkIncorrect: (index: number) => void;
  };

export function useCombinationAnswers(): UseCombinationAnswersReturn {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });
  const { t: tCombination } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.combinationForm',
  });

  const { control, getValues, setValue, watch } =
    useFormContext<CombinationFormFields>();
  const {
    fieldState: { error },
  } = useController({ control, name: 'answers' });

  const { focus, clearIfMatches, isFocused } = useSpotlight();

  const questions = watch('questions') ?? [];

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'answers',
    keyName: 'fieldId',
    rules: {
      validate: (answers = []) =>
        validateCombinationAnswers(answers, questions, t, tCombination),
    },
  });

  const handleAdd = () => {
    append({ validPositions: [] });
    focus(fields.length);
  };

  const handleUpdateText = (index: number, text: string) => {
    const current = getValues(`answers.${index}`);
    update(index, { ...current, text, mediaFile: null, altText: '' });
    clearIfMatches(index);
  };

  const handleUpdateMedia = (
    index: number,
    mediaFile: File | string,
    altText?: string,
  ) => {
    const current = getValues(`answers.${index}`);
    update(index, {
      ...current,
      mediaFile,
      altText: altText ?? null,
      text: '',
    });
    clearIfMatches(index);
  };

  const handleUpdateValidPositions = (
    index: number,
    validPositions: number[],
  ) => {
    setValue(`answers.${index}.validPositions`, validPositions);
    clearIfMatches(index);
  };

  const handleMarkIncorrect = (index: number) => {
    setValue(`answers.${index}.validPositions`, []);
    clearIfMatches(index);
  };

  const handleDelete = (index: number) => {
    remove(index);
    clearIfMatches(index);
  };

  const isNewItem = ({ index }: IsNewItemArgs) => isFocused(index);

  return {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleUpdateValidPositions,
    handleMarkIncorrect,
    handleDelete,
    isNewItem,
  };
}
